/**
 * How a printed box reaches the card that owns the record.
 *
 * Committing one box writes the whole record, so a box needs more than its own
 * fields. This lives apart from `DescriptorCard` because a table's rows commit
 * themselves: having them import the card directly would close a cycle through
 * `DescriptorSectionField`.
 */

import { createContext, useContext } from 'react';
import type { FieldValues } from 'react-hook-form';

export type DescriptorSectionApi = {
  /** Server-backed values, the base every commit is layered onto. */
  values: FieldValues;
  disabled: boolean;
  /**
   * Writes the whole record with only these fields taken from the form.
   *
   * `override` supplies a field's value directly, for callers that mutate a
   * field array and cannot wait for the form state to catch up.
   */
  commit: (sources: string[], override?: Record<string, unknown>) => void;
  /** Drops edits to these fields, back to what the server last gave us. */
  revert: (sources: string[]) => void;
};

export const DescriptorSectionContext =
  createContext<DescriptorSectionApi | null>(null);

export const useDescriptorSection = (): DescriptorSectionApi => {
  const api = useContext(DescriptorSectionContext);
  if (!api) {
    throw new Error(
      'useDescriptorSection must be used inside a DescriptorCard'
    );
  }
  return api;
};
